import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/index';
import LoginModal from './LoginModal';
import ProfilModal from './ProfilModal';
import AcademicYearSelector from './AcademicYearSelector';
import './Header.css';
import { API_ENDPOINTS } from '../config/api';
import API_BASE_URL from '../config/api';
import { isArchivedYear } from '../config/academicYear';

const Header = () => {
  const { t } = useTranslation();
  const [isVisible, setVisible] = useState(true);
  const [currentLang, setCurrentLang] = useState(localStorage.getItem('appLanguage') || 'tr');
  const [archiveReadOnly, setArchiveReadOnly] = useState(isArchivedYear);

  useEffect(() => {
    const updateArchiveStatus = () => setArchiveReadOnly(isArchivedYear());
    window.addEventListener('academic-year-meta-changed', updateArchiveStatus);
    return () => window.removeEventListener('academic-year-meta-changed', updateArchiveStatus);
  }, []);

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('appLanguage', lang);
    setCurrentLang(lang);
  };
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isProfilModalOpen, setIsProfilModalOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);
  const [logoSrc, setLogoSrc] = useState('/Kostu_Logo_Tek.png');

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = currentScrollY - lastScrollY;

      // Scroll aşağı yönde ve 20 pikselden fazla
      if (scrollDifference > 20) {
        setVisible(false);
      }
      // Scroll yukarı yönde ve 20 pikselden fazla
      else if (scrollDifference < -20) {
        setVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Kullanıcı bilgilerini sessionStorage'dan al
  useEffect(() => {
    const userRole = sessionStorage.getItem('userRole');
    const akademisyenId = sessionStorage.getItem('akademisyen_id');
    
    if (userRole === 'akademisyen' && akademisyenId) {
      // Akademisyen bilgilerini API'den al
      fetch(`${API_BASE_URL}/api/akademisyen/${akademisyenId}`)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then(async (data) => {
          if (data.success) {
            // Akademisyen güncel tablosundan name ve surname bilgilerini de al
            try {
              const guncelResponse = await fetch(API_ENDPOINTS.AKADEMISYEN_GUNCEL_BY_ID(akademisyenId));
              if (!guncelResponse.ok) {
                throw new Error(`HTTP error! status: ${guncelResponse.status}`);
              }
              const guncelData = await guncelResponse.json();
              
              if (guncelResponse.ok && guncelData) {
                const userData = {
                  ...data.user,
                  name: guncelData.name,
                  surname: guncelData.surname
                };
                setUserInfo(userData);
              } else {
                setUserInfo(data.user);
              }
            } catch (error) {
              setUserInfo(data.user);
            }
          }
        })
        .catch(() => {});
    }
  }, []);

  const isHomePage = location.pathname === '/';

  const handleLoginClick = (e) => {
    e.preventDefault();
    setIsLoginModalOpen(true);
  };

  const handleLoginClose = () => {
    setIsLoginModalOpen(false);
  };

  const handleProfilClose = () => {
    setIsProfilModalOpen(false);
  };

  const handleLogin = (loginData) => {
    // Login başarılı olduğunda kullanıcı bilgilerini güncelle
    if (loginData) {
      setUserInfo(loginData);
    }
    // Here you can add actual login logic
    // For now, just redirect to the management panel
    window.location.href = '/dashboard';
  };

  const handleLogout = () => {
    const wasAdmin = sessionStorage.getItem('userRole') === 'admin';
    const finishLogout = () => {
      sessionStorage.removeItem('userRole');
      sessionStorage.removeItem('akademisyen_id');
      setUserInfo(null);
      window.location.href = '/';
    };
    if (wasAdmin) {
      fetch(API_ENDPOINTS.ADMIN_LOGOUT, {
        method: 'POST',
        credentials: 'include',
      }).finally(finishLogout);
    } else {
      finishLogout();
    }
  };

  
  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleProfilIconClick = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleDropdownProfil = () => {
    setIsProfilModalOpen(true);
    setIsDropdownOpen(false);
  };

  const handleDropdownLogout = () => {
    handleLogout();
    setIsDropdownOpen(false);
  };

  const handleLogoError = () => {
    // Fallback: olası bozuk dosya adı varyasyonu
    if (logoSrc !== '/Kostu_Logo_Tek.png') {
      setLogoSrc('/Kostu_Logo_Tek.png');
    }
  };

  const handleLogoClick = () => {
    // Eğer kullanıcı giriş yapmışsa dashboard'a git
    if (userInfo) {
      navigate('/dashboard');
    }
  };

  const getUserInitials = () => {
    const fullName = `${userInfo?.name || ''} ${userInfo?.surname || ''}`.trim();
    if (fullName) {
      return fullName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('');
    }

    const mail = userInfo?.email || userInfo?.username || '';
    if (mail) {
      return mail.slice(0, 2).toUpperCase();
    }

    return 'ST';
  };

  const storedRole = (sessionStorage.getItem('userRole') || '').toLowerCase();
  const canSelectAcademicYear = !['student', 'ogrenci', 'öğrenci'].includes(storedRole);

  return (
    <>
      <header className={`header ${!isVisible ? 'hidden' : ''} ${isHomePage ? 'header-transparent' : ''}`}>
        <div className="header-content">
          <div className="header-left">
            <img 
              src={logoSrc}
              onError={handleLogoError}
              alt="KOSTÜ Logo" 
              className="logo"
              onClick={handleLogoClick}
              style={{ cursor: userInfo ? 'pointer' : 'default' }}
            />
          </div>
          {!isHomePage && (
            <div className="header-center">
              <h1 className="header-title">{t('header.title')}</h1>
            </div>
          )}
          <div className="header-right" style={{position:'relative'}}>
            {(userInfo || Boolean(storedRole)) && canSelectAcademicYear && <AcademicYearSelector />}
            <div className="header-lang-switcher">
              <button
                className={`header-lang-btn ${currentLang === 'tr' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('tr')}
                title="Türkçe"
              >
                <img src="/tr-flag.svg" alt="TR" className="lang-flag" />
              </button>
              <button
                className={`header-lang-btn ${currentLang === 'en' ? 'active' : ''}`}
                onClick={() => handleLanguageChange('en')}
                title="English"
              >
                <img src="/en-flag.svg" alt="EN" className="lang-flag" />
              </button>
            </div>
            {userInfo ? (
              <>
                <button
                  onClick={handleProfilIconClick}
                  className="profil-icon-button"
                  title="Profil"
                  aria-haspopup="true"
                  aria-expanded={isDropdownOpen}
                >
                  <span className="profil-initials" aria-label={t('header.profile')}>
                    {getUserInitials()}
                  </span>
                </button>
                {isDropdownOpen && (
                  <div className="profil-dropdown-menu" ref={dropdownRef}>
                    <button className="profil-dropdown-item" onClick={handleDropdownProfil}>{t('header.profile')}</button>
                    <button className="profil-dropdown-item" onClick={handleDropdownLogout}>{t('header.logout')}</button>
                  </div>
                )}
              </>
            ) : isHomePage ? (
              <button onClick={handleLoginClick} className="login-button">
                {t('header.login')}
              </button>
            ) : null}
          </div>
        </div>
      </header>
      {archiveReadOnly && !['student', 'ogrenci', 'öğrenci'].includes(
        (sessionStorage.getItem('userRole') || '').toLocaleLowerCase('tr-TR')
      ) && (
        <div className="archive-global-notice" role="status">
          <span className="archive-global-notice-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="5" y="10" width="14" height="11" rx="2" />
              <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            </svg>
          </span>
          <span>{t('header.archiveReadOnlyNotice')}</span>
        </div>
      )}
      
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={handleLoginClose}
        onLogin={handleLogin}
      />
      
      <ProfilModal 
        isOpen={isProfilModalOpen}
        onClose={handleProfilClose}
        akademisyenId={userInfo?.id}
      />
    </>
  );
};

export default Header;
